import React, { useMemo } from 'react';
import { BsLink } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Stack
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Authorizer, { FeatureFlags } from '../../../../../../../../../components/Authorizer';
import { analyticsIds } from '../../../../../../../../../constants/analyticsIds';
import * as Entities from '../../../../../../../../../constants/entities';
import { itemSearch } from '../../../../../../../../../redux/modules/items';
import { hideModal, showModal } from '../../../../../../../../../redux/modules/modal';
import { useActiveRecordDocuments, useActiveRecordPolicies } from '../../../../../../../../../services/record-service';

import ActivityLogMultiSelectSearchWrapper from './ActivityLogMultiSelectSearchWrapper';
import ActivityLogMultiSelectWrapper from './ActivityLogMultiSelectWrapper';

const ActivityLogLinkPopover = ({ insuredId, recordId, jobId }) => {
  const dispatch = useDispatch();
  const { data: recordPolicies, isLoading: recordPoliciesLoading } = useActiveRecordPolicies(recordId);
  const { data: recordDocuments, isLoading: recordDocumentsLoading } = useActiveRecordDocuments({ recordId });

  const activePolicies = useMemo(() => (recordPoliciesLoading ? [] : recordPolicies), [recordPolicies, recordPoliciesLoading]);
  const activeDocuments = useMemo(() => (recordDocumentsLoading ? [] : recordDocuments), [recordDocuments, recordDocumentsLoading]);

  const generateDocumentsChecklist = data => data.map(document => ({
    checked: false,
    description: document.displayName || document.originalName,
    id: document.id,
    type: Entities.InsuranceDocument
  }));

  const generateItemsChecklist = data => data.map(item => {
    const unitNumber = item.propertyValues?.unitNumber;
    const year = item.propertyValues?.modelYear;
    const make = item.propertyValues?.vehMake;
    const model = item.propertyValues?.vehModel;
    const VIN = item.propertyValues?.vinNbr;

    return {
      checked: false,
      description: `${unitNumber} | ${VIN} | ${year}/${make}/${model}`,
      id: item.id,
      type: Entities.Item
    };
  });

  const generatePoliciesChecklist = data => data.map(policy => ({
    checked: false,
    description: `${policy.insurancePolicyType?.displayName} | ${policy.number}`,
    id: policy.id,
    type: Entities.InsurancePolicy
  }));

  const policiesChecklist = generatePoliciesChecklist(activePolicies);
  const documentsChecklist = generateDocumentsChecklist(activeDocuments);

  function handleApplyLinkedElements() {
    dispatch(hideModal());
  }

  return (
    <IconButton
      data-analytics-id={analyticsIds.insuredProfilePage.activityLog.openLinkModal}
      icon={<Icon as={BsLink} boxSize={6} />}
      variant="textPrimary"
      onClick={() => dispatch(showModal(() => (
        <Box pb={5}>
          <Stack flexBasis="100%" minWidth="600px" spacing={3}>
            <Authorizer featureFlag={FeatureFlags.Codes}>
              <ActivityLogMultiSelectSearchWrapper
                formatDataFunction={generateItemsChecklist}
                minWidth="660px"
                searchBlueprint={itemSearch}
                title="Units | ViN | Year/Make/Model"
                type={Entities.Item}
              />
            </Authorizer>
            <ActivityLogMultiSelectWrapper
              checklist={documentsChecklist}
              minWidth="720px"
              title="Documents"
              type={Entities.InsuranceDocument}
            />
            <HStack alignItems="flex-end" as={Flex} spacing={4}>
              <Box minW="400px">
                <ActivityLogMultiSelectWrapper
                  checklist={policiesChecklist}
                  minWidth="400px"
                  title="Policies"
                  type={Entities.InsurancePolicy}
                />
              </Box>
              <Button
                data-analytics-id={analyticsIds.insuredProfilePage.activityLog.applyLinkButton}
                flexBasis="50%"
                maxWidth="50%"
                minWidth="100px"
                onClick={() => handleApplyLinkedElements()}
              >
                Apply
              </Button>
            </HStack>
          </Stack>
        </Box>
      ), {
        size: '3xl',
        title: 'Link Comments To:',
        center: false
      }))}
    />
  );
};

ActivityLogLinkPopover.propTypes = {
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default ActivityLogLinkPopover;
